/**
 * Modern Front Desk Summary Cards - 2026 Edition
 * Vibrant, colorful cards with gradients and glass-morphism
 */
import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Home, ArrowLeft, ArrowRight, TrendingUp, Sparkles } from 'lucide-react';
import { getRoomTypeColor } from '../../utils/roomTypeColors';

const getNightsBadgeColor = (nights) => {
  if (nights === 1) return 'bg-gradient-to-r from-rose-500 to-pink-500 text-white border-0';
  if (nights === 2) return 'bg-gradient-to-r from-orange-500 to-amber-500 text-white border-0';
  if (nights <= 4) return 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white border-0';
  return 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white border-0';
};

export const SummaryCards = ({ summary, onCardClick }) => {
  const cards = [
    {
      id: 'in_house',
      title: 'In-House Guests',
      iconUrl: 'https://customer-assets.emergentagent.com/job_easy-stay-ai/artifacts/bdeqm8gg_In%20House%20Guest.png',
      gradient: 'from-blue-500 via-cyan-500 to-teal-500',
      iconBg: 'from-blue-400 to-cyan-500',
      glowColor: 'shadow-blue-500/20',
      accentColor: 'text-blue-600',
      count: summary.in_house?.count || 0,
      guests: summary.in_house?.guests || [],
      description: 'Currently staying'
    },
    {
      id: 'arrivals',
      title: 'Arrivals Today',
      iconUrl: 'https://customer-assets.emergentagent.com/job_easy-stay-ai/artifacts/i631z23a_Arrival.png',
      gradient: 'from-emerald-500 via-green-500 to-lime-500',
      iconBg: 'from-emerald-400 to-green-500',
      glowColor: 'shadow-emerald-500/20',
      accentColor: 'text-emerald-600',
      count: summary.arrivals_today?.count || 0,
      guests: summary.arrivals_today?.guests || [],
      description: 'Expected check-ins',
      backgroundImage: 'https://thumbs.dreamstime.com/b/receptionist-guest-manager-customers-hotel-reception-desk-concierge-service-people-arrival-check-lobby-man-86359991.jpg'
    },
    {
      id: 'departures',
      title: 'Departures Today',
      iconUrl: 'https://customer-assets.emergentagent.com/job_easy-stay-ai/artifacts/cl4k4plp_Check%20out%20icon.png',
      gradient: 'from-amber-500 via-orange-500 to-red-500',
      iconBg: 'from-amber-400 to-orange-500',
      glowColor: 'shadow-amber-500/20',
      accentColor: 'text-amber-600',
      count: summary.departures_today?.count || 0,
      guests: summary.departures_today?.guests || [],
      description: 'Scheduled check-outs'
    }
  ];

  return (
    <TooltipProvider delayDuration={200}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {cards.map((card) => {
          return (
            <Tooltip key={card.id}>
              <TooltipTrigger asChild>
                <Card
                  className={`
                    group relative overflow-hidden cursor-pointer
                    bg-gradient-to-br from-white to-gray-50
                    border border-gray-200/50
                    hover:border-transparent
                    hover:shadow-2xl ${card.glowColor}
                    transition-all duration-500 ease-out
                    hover:scale-[1.02] hover:-translate-y-1
                  `}
                  onClick={() => onCardClick(card.id)}
                >
                  {/* Background Image for Arrivals */}
                  {card.backgroundImage && (
                    <>
                      <div 
                        className="absolute inset-0 bg-cover bg-center opacity-15 group-hover:opacity-20 transition-opacity duration-500"
                        style={{ backgroundImage: `url(${card.backgroundImage})` }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 via-green-500/5 to-transparent" />
                    </>
                  )}
                  
                  {/* Gradient overlay on hover */}
                  <div className={`
                    absolute inset-0 opacity-0 group-hover:opacity-10
                    bg-gradient-to-br ${card.gradient}
                    transition-opacity duration-500
                  `} />
                  
                  {/* Animated sparkle effect */}
                  <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300">
                    <Sparkles className="w-4 h-4 text-yellow-400 animate-pulse" />
                  </div>

                  <CardContent className="p-6 relative z-10">
                    {/* Icon at top - Cropped to show only center illustration */}
                    <div className="flex justify-center mb-4">
                      <div className="w-20 h-20 overflow-hidden cursor-pointer hover:scale-110 transition-transform duration-300 flex items-center justify-center">
                        <img 
                          src={card.iconUrl} 
                          alt={card.title}
                          className="w-32 h-32 object-contain drop-shadow-xl"
                          style={{ transform: 'scale(1.6)' }}
                        />
                      </div>
                    </div>

                    {/* Count Display - Centered */}
                    <div className="mb-4 text-center">
                      <div className="flex items-baseline justify-center gap-2">
                        <span className={`
                          text-6xl font-black
                          bg-gradient-to-br ${card.gradient}
                          bg-clip-text text-transparent
                        `}>
                          {card.count}
                        </span>
                      </div>
                      <span className="text-sm font-semibold text-gray-500 mt-1 block">
                        {card.count === 1 ? 'guest' : 'guests'}
                      </span>
                      
                      {/* Trending indicator */}
                      {card.count > 0 && (
                        <div className="flex items-center justify-center gap-1 mt-2">
                          <TrendingUp className="w-3 h-3 text-green-500" />
                          <span className="text-xs font-medium text-green-600">Active</span>
                        </div>
                      )}
                    </div>

              {/* Guest Preview */}
              {card.guests.length > 0 && (
                <div className="space-y-3">
                  <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
                  
                  <div className="space-y-2">
                    {card.guests.slice(0, 2).map((guest, idx) => {
                      const roomColors = getRoomTypeColor(guest.room_type);
                      
                      return (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-2.5 rounded-xl bg-gradient-to-r from-gray-50 to-transparent hover:from-gray-100 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          {/* Avatar */}
                          <div className={`
                            w-8 h-8 rounded-full
                            bg-gradient-to-br ${card.iconBg}
                            flex items-center justify-center
                            text-white text-xs font-bold
                            shadow-md
                          `}>
                            {guest.guest_name?.charAt(0) || '?'}
                          </div>
                          
                          {/* Guest info */}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">
                              {guest.guest_name}
                            </p>
                            {/* Colored Room Type Badge */}
                            {guest.room_type && (
                              <div className="flex items-center gap-1 mt-0.5">
                                <span className={`${roomColors.badge} text-white px-2 py-0.5 rounded text-xs font-semibold`}>
                                  {roomColors.icon} {guest.room_type}
                                </span>
                              </div>
                            )}
                            {!guest.room_type && guest.assigned_room_number && (
                              <p className="text-xs text-gray-500">
                                Room {guest.assigned_room_number}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        {/* Nights badge */}
                        {guest.nights && (
                          <Badge className={`${getNightsBadgeColor(guest.nights)} px-2.5 py-0.5 text-xs font-bold shadow-sm`}>
                            {guest.nights}N
                          </Badge>
                        )}
                      </div>
                    )})}
                  </div>

                  {/* View all link */}
                  {card.guests.length > 2 && (
                    <button className={`
                      w-full py-2 px-4 rounded-xl
                      bg-gradient-to-r ${card.gradient}
                      text-white text-sm font-semibold
                      hover:shadow-lg hover:shadow-black/20
                      transform hover:scale-[1.02]
                      transition-all duration-300
                    `}>
                      View all {card.guests.length} guests →
                    </button>
                  )}
                </div>
              )}

              {/* Empty state */}
              {card.count === 0 && (
                <div className="text-center py-4">
                  <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center p-2">
                    <img 
                      src={card.iconUrl} 
                      alt={card.title}
                      className="w-full h-full object-contain opacity-30"
                    />
                  </div>
                  <p className="text-sm text-gray-400 font-medium">No guests</p>
                </div>
              )}
            </CardContent>

            {/* Bottom gradient accent */}
            <div className={`
              h-1 w-full
              bg-gradient-to-r ${card.gradient}
              opacity-0 group-hover:opacity-100
              transition-opacity duration-500
            `} />
          </Card>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="px-4 py-2">
          <p className="font-bold text-base">{card.title}</p>
          <p className="text-xs text-gray-400 mt-0.5">{card.description}</p>
        </TooltipContent>
      </Tooltip>
        );
      })}
    </div>
    </TooltipProvider>
  );
};
